'use client';
import Script from 'next/script';
import { useEffect } from 'react';
import { useConsent } from './ConsentProvider';
import {
  ga4ConfigScript, gtmHeadScript, gtmBodyNoscript, fbPixelScript, fbPixelNoscript,
  loadClarity, signalClarityConsentV2,
} from './siteAnalytics.helpers';

export interface AnalyticsSettings {
  googleAnalyticsId?: string;
  googleTagManagerId?: string;
  facebookPixelId?: string;
  microsoftClarityId?: string;
  headScripts?: string;
}

export function SiteAnalytics({ settings }: { settings: AnalyticsSettings }) {
  const { record } = useConsent();
  const analytics = record?.analytics === true;
  const marketing = record?.marketing === true;
  const { googleAnalyticsId: ga, googleTagManagerId: gtm, facebookPixelId: fb, microsoftClarityId: clarity, headScripts } = settings;

  // Clarity is loaded here, from an effect that runs AFTER hydration — never via a
  // next/script during React's commit (that threw "a[c] is not a function" and took
  // hydration down with it → #418). It also needs an explicit ConsentV2 signal for
  // EEA/UK/CH visitors, or the tag stays in no-consent mode ("track":false).
  // Load only after analytics is granted; reflect grant/revoke to Clarity; nothing
  // while the choice is still pending.
  useEffect(() => {
    if (!clarity || !record) return;
    const w = window as { clarity?: unknown };
    if (analytics) {
      loadClarity(clarity);
      signalClarityConsentV2(w, true, marketing);
    } else if (typeof w.clarity === 'function') {
      // granted earlier, now revoked — tell the already-loaded tag to stop
      signalClarityConsentV2(w, false, marketing);
    }
  }, [clarity, record, analytics, marketing]);

  return (
    <>
      {/* Google tags always load — Consent Mode keeps them cookieless until granted. */}
      {gtm && (
        <>
          <Script id="gtm" strategy="afterInteractive">{gtmHeadScript(gtm)}</Script>
          <noscript dangerouslySetInnerHTML={{ __html: gtmBodyNoscript(gtm) }} />
        </>
      )}
      {ga && (
        <>
          <Script id="ga4-src" src={`https://www.googletagmanager.com/gtag/js?id=${ga}`} strategy="afterInteractive" />
          <Script id="ga4" strategy="afterInteractive">{ga4ConfigScript(ga)}</Script>
        </>
      )}

      {/* Non-Google tags do not honour Consent Mode — mount only after the relevant category is granted. */}
      {fb && marketing && (
        <>
          <Script id="fb-pixel" strategy="afterInteractive">{fbPixelScript(fb)}</Script>
          <noscript dangerouslySetInnerHTML={{ __html: fbPixelNoscript(fb) }} />
        </>
      )}
      {/* Clarity is loaded in the effect above (post-hydration), not as a <Script>. */}
      {headScripts && analytics && (
        <Script id="head-scripts" strategy="afterInteractive" dangerouslySetInnerHTML={{ __html: headScripts }} />
      )}
    </>
  );
}
